import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, DropletsIcon, LeafIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';
import { IMAGES, INGREDIENTS, TRUST_ITEMS } from '../data/content';
import { Eyebrow, Reveal, Section } from '../components/Section';
const BOTANICALS = [{
  name: 'Amla',
  role: 'Antioxidant support',
  note: 'A bright Indian superfruit, naturally rich in vitamin C and plant antioxidants.'
}, {
  name: 'Ashwagandha',
  role: 'Everyday vitality',
  note: 'A revered botanical traditionally used to support balance through busy days.'
}, {
  name: 'Guduchi',
  role: 'Daily resilience',
  note: 'A climbing herb long valued in traditional Indian wellness practices.'
}, {
  name: 'Brahmi',
  role: 'Clarity & focus',
  note: 'A gentle botanical traditionally included in formulas for focused daily rituals.'
}];
const NUTRIENTS = [{
  group: 'Vitamins',
  items: 'A, C, D, E, K & B-complex'
}, {
  group: 'Minerals',
  items: 'Zinc, magnesium, calcium & iron'
}, {
  group: 'Plant nutrition',
  items: 'Botanical extracts & greens'
}, {
  group: 'Everyday format',
  items: 'One 7g single-serve sachet'
}];
const STANDARDS = [{
  icon: LeafIcon,
  title: 'Plant based',
  copy: 'Thoughtfully selected botanical ingredients, without animal-derived additions.'
}, {
  icon: DropletsIcon,
  title: 'Made to mix',
  copy: 'A smooth, simple powder format designed for your daily glass of water.'
}, {
  icon: ShieldCheckIcon,
  title: 'Made with care',
  copy: 'Produced in an FDA registered facility.'
}];
export function IngredientsPage() {
  return <main className="overflow-hidden pt-20" data-id="element-417" data-mp="wzvd0k0pmrkk">
      <section className="relative min-h-[590px] w-full overflow-hidden bg-forest-deep px-6 py-16 text-ivory md:px-10 md:py-20 lg:px-16" data-id="element-418" data-mp="x0o98r21lyog">
        <img src={IMAGES.ingredientsHero} alt="Amla, herbs, roots and green nutrition powder arranged on a stone surface" className="absolute inset-0 h-full w-full object-cover object-[62%_center] opacity-65" data-id="element-419" data-mp="s34w5uh2zry3" />
        <div className="absolute inset-0 bg-forest-deep/55" aria-hidden="true" data-id="element-420" data-mp="35idd62v9a750" />
        <div className="relative mx-auto flex min-h-[460px] max-w-7xl flex-col justify-end" data-id="element-421" data-mp="3aaof1b9k4307">
          <Reveal className="max-w-3xl" data-id="element-422" data-mp="2wizz0ww6sudy">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage" data-id="element-423" data-mp="2wkb553d3ue88">Inside Medufit</p>
            <h1 className="mt-5 font-serif text-5xl font-medium leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl" data-id="element-424" data-mp="31icrbx5tutnx">
              Every ingredient earns its place.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-ivory/85" data-id="element-425" data-mp="3t7plz1mo8d6s">
              A daily blend of Indian botanicals, essential vitamins, and minerals — selected to make
              simple, plant-based nutrition easier to return to every morning.
            </p>
          </Reveal>
        </div>
      </section>

      <Section className="py-16 md:py-20" data-id="element-426" data-mp="23srjlct1co32">
        <Reveal className="mx-auto max-w-3xl text-center" data-id="element-427" data-mp="28toz1hmnv40v">
          <Eyebrow data-id="element-428" data-mp="28r2rvj3omqj6">Our formulation philosophy</Eyebrow>
          <h2 className="mt-5 font-serif text-4xl font-medium leading-tight text-forest sm:text-5xl" data-id="element-429" data-mp="218ldaygy8ljc">
            Start with nature. Add only what supports the ritual.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-forest/70 sm:text-lg" data-id="element-430" data-mp="2aaulhhoqw2tk">
            We designed Medufit around a straightforward idea: bring together the plant botanicals
            people already trust with the nutritional essentials that help round out a modern day.
          </p>
        </Reveal>

        <div className="mx-auto mt-12 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-431" data-mp="1dkv6p5i0482j">
          {[['04', 'Indian botanicals'], ['21', 'Vitamins & minerals'], ['0g', 'Added sugar'], ['01', 'Daily sachet']].map(([stat, label], index) => <Reveal key={label} delay={index * 0.07} data-id="element-432" data-mp="yu4cxrwumyws" data-mpi={index}>
              <div className="rounded-2xl border border-forest/10 bg-beige/25 p-6 text-center" data-id="element-433" data-mp="tw2tv6zfazlb">
                <p className="font-serif text-4xl text-forest" data-id="element-434" data-mp="tu8fycmkk7qq">{stat}</p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-forest/60" data-id="element-435" data-mp="17kmqec98f7i2">{label}</p>
              </div>
            </Reveal>)}
        </div>
      </Section>

      <section className="bg-forest-deep px-6 py-16 text-ivory md:px-10 md:py-20 lg:px-16" data-id="element-436" data-mp="1hfepjkigq2ey">
        <div className="mx-auto max-w-7xl" data-id="element-437" data-mp="1m993s8fatp6n">
          <Reveal className="max-w-2xl" data-id="element-438" data-mp="1q9b6mkwrsoni">
            <Eyebrow data-id="element-439" data-mp="1q89mlqxs0rtn">Four parts, one daily blend</Eyebrow>
            <h2 className="mt-5 font-serif text-4xl font-medium leading-tight sm:text-5xl" data-id="element-440" data-mp="1v8e4nlec1ulz">
              Designed to work better together.
            </h2>
          </Reveal>

          <div className="mt-10 flex gap-5 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:grid md:grid-cols-2 md:overflow-visible md:pb-0" data-id="element-441" data-mp="205e6tl82ad7t">
            {INGREDIENTS.map((ingredient, index) => <Reveal key={ingredient.name} delay={index * 0.08} className="w-[300px] shrink-0 md:w-auto" data-id="element-442" data-mp="as9cng4otge7" data-mpi={index}>
                <article className="h-full overflow-hidden rounded-2xl border border-ivory/15 bg-forest/30 sm:grid sm:grid-cols-[0.84fr_1.16fr]" data-id="element-443" data-mp="2o3c701m1xb2p">
                  <img src={IMAGES[ingredient.image]} alt={`${ingredient.name} ingredient group`} className="h-52 w-full object-cover sm:h-full" data-id="element-444" data-mp="2jast13jgcdxd" />
                  <div className="p-7" data-id="element-445" data-mp="2a9kvwoso9j4v">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sage" data-id="element-446" data-mp="371y4z7m9um2e">Medufit blend</p>
                    <h3 className="mt-3 font-serif text-2xl text-ivory" data-id="element-447" data-mp="331ea6ap67ujf">{ingredient.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-ivory/75" data-id="element-448" data-mp="2xzx8icyv441s">{ingredient.desc}</p>
                    <ul className="mt-5 space-y-2" data-id="element-449" data-mp="2t0u8x81niq17">
                      {ingredient.bullets.map(bullet => <li key={bullet} className="flex items-center gap-2 text-sm font-medium text-ivory/90" data-id="element-450" data-mp="1mnecl6dssgzc">
                          <CheckIcon className="h-4 w-4 shrink-0 text-sage" data-id="element-451" data-mp="1hlfcc5dcu06m" />
                          {bullet}
                        </li>)}
                    </ul>
                  </div>
                </article>
              </Reveal>)}
          </div>
        </div>
      </section>

      <Section className="py-16 md:py-20" data-id="element-452" data-mp="1a5k0n72khmio">
        <div className="grid items-start gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:gap-20" data-id="element-453" data-mp="1f1977ud083oh">
          <Reveal data-id="element-454" data-mp="10d5y4c3o0bvg">
            <Eyebrow data-id="element-455" data-mp="10cmhqztnxvjj">The botanical ledger</Eyebrow>
            <h2 className="mt-5 font-serif text-4xl font-medium leading-tight text-forest sm:text-5xl" data-id="element-456" data-mp="1544ibmrrxu3z">
              Rooted in Indian botanical wisdom.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-forest/70" data-id="element-457" data-mp="rekjgs1bdgzy">
              We look to recognizable botanicals with a long place in everyday wellness rituals — then
              bring them into a format made for modern life.
            </p>
          </Reveal>
          <div className="divide-y divide-forest/12 border-t border-forest/12" data-id="element-458" data-mp="mcbrnz06gkzf">
            {BOTANICALS.map((botanical, index) => <Reveal key={botanical.name} delay={index * 0.07} data-id="element-459" data-mp="2to7ajjt37xd" data-mpi={index}>
                <article className="grid gap-2 py-6 sm:grid-cols-[0.7fr_0.7fr_1.6fr] sm:items-baseline sm:gap-5" data-id="element-460" data-mp="3tzpak5xj4x8g">
                  <h3 className="font-serif text-2xl text-forest" data-id="element-461" data-mp="crc0v65oe9dy">{botanical.name}</h3>
                  <p className="text-sm font-medium text-sage" data-id="element-462" data-mp="7pd1e7d23wma">{botanical.role}</p>
                  <p className="text-sm leading-relaxed text-forest/70" data-id="element-463" data-mp="lpec0ej4iiey">{botanical.note}</p>
                </article>
              </Reveal>)}
          </div>
        </div>
      </Section>

      <Section className="pb-16 md:pb-20" data-id="element-464" data-mp="vfh5efu5pydt">
        <div className="grid items-center overflow-hidden rounded-[2rem] bg-beige/35 lg:grid-cols-2" data-id="element-465" data-mp="vid1rzqcwf05">
          <Reveal className="order-2 p-8 sm:p-12 lg:order-1 lg:p-16" data-id="element-466" data-mp="qjs15z76ztb6">
            <Eyebrow data-id="element-467" data-mp="14hqer2dovlpm">The everyday essentials</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl font-medium leading-tight text-forest sm:text-4xl" data-id="element-468" data-mp="10g529i52h9fy">
              The vitamins and minerals that complete the picture.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-forest/70" data-id="element-469" data-mp="2bte2jqzdfdg3">
              Alongside botanicals, Medufit includes essential micronutrients to help simplify the
              everyday work of supporting your nutritional routine.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-470" data-mp="2ktjbi5k7qjwd">
              {NUTRIENTS.map(nutrient => <div key={nutrient.group} className="rounded-xl border border-forest/10 bg-ivory/75 p-4" data-id="element-471" data-mp="1o1ywpo8dye03">
                  <p className="font-serif text-lg text-forest" data-id="element-472" data-mp="1t11zf20w884w">{nutrient.group}</p>
                  <p className="mt-1 text-xs leading-relaxed text-forest/65" data-id="element-473" data-mp="1x3gnc0jf5y31">{nutrient.items}</p>
                </div>)}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="order-1 h-full min-h-[320px] lg:order-2" data-id="element-474" data-mp="15ciw70wpaljo">
            <img src={IMAGES.ingredientsQuality} alt="Thoughtfully arranged botanicals and jars in a sunlit studio" className="h-full w-full object-cover" data-id="element-475" data-mp="19enonwbexc4q" />
          </Reveal>
        </div>
      </Section>

      <section className="border-y border-forest/10 bg-forest/6 px-6 py-16 md:px-10 md:py-20 lg:px-16" data-id="element-476" data-mp="wugynuxjorga">
        <div className="mx-auto max-w-7xl" data-id="element-477" data-mp="swsz2ekwqai2">
          <Reveal className="mx-auto max-w-2xl text-center" data-id="element-478" data-mp="nutskvtvexgw">
            <Eyebrow data-id="element-479" data-mp="iws949sj8w4w">Our standards</Eyebrow>
            <h2 className="mt-5 font-serif text-4xl font-medium leading-tight text-forest sm:text-5xl" data-id="element-480" data-mp="ixtvhaf0151x">
              Straightforward nutrition. No unnecessary extras.
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-481" data-mp="97s8yn2anup4">
            {STANDARDS.map((standard, index) => {
            const Icon = standard.icon;
            return <Reveal key={standard.title} delay={index * 0.08} data-id="element-482" data-mp="6kb263owvvk" data-mpi={index}>
                  <article className="h-full rounded-2xl border border-forest/10 bg-ivory/75 p-7 text-center" data-id="element-483" data-mp="3rclrku6fchol">
                    <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-forest text-ivory" data-id="element-484" data-mp="zqlq5eohraai">
                      <Icon className="h-5 w-5" data-id="element-485" data-mp="vo75qq56c2h4" />
                    </span>
                    <h3 className="mt-5 font-serif text-2xl text-forest" data-id="element-486" data-mp="19gdlib4f07g1">{standard.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-forest/70" data-id="element-487" data-mp="14htz69lwstn5">{standard.copy}</p>
                  </article>
                </Reveal>;
          })}
          </div>
        </div>
      </section>

      <section className="bg-forest-deep px-6 py-16 text-center text-ivory md:px-10 md:py-20 lg:px-16" data-id="element-488" data-mp="cr60d47pk0cn">
        <Reveal className="mx-auto max-w-2xl" data-id="element-489" data-mp="qr6zae4ewxph">
          <SparklesIcon className="mx-auto h-6 w-6 text-sage" data-id="element-490" data-mp="qo2q12c3bbry" />
          <h2 className="mt-5 font-serif text-4xl font-medium leading-tight sm:text-5xl" data-id="element-491" data-mp="lqay48bg3pnr">
            One simple way to show up for yourself.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-ivory/75" data-id="element-492" data-mp="1x6hocg9s435z">
            Bring thoughtful plant-based nutrition into the rhythm of your every day.
          </p>
          <Link to="/#packages" className="mt-8 inline-flex items-center gap-2 rounded-full bg-ivory px-7 py-3.5 text-sm font-semibold text-forest transition-colors hover:bg-beige" data-id="element-493" data-mp="3k2xnvth69hvi">
            Shop Medufit <ArrowRightIcon className="h-4 w-4" data-id="element-494" data-mp="3b1hdvmu9rhrt" />
          </Link>
        </Reveal>
      </section>

      <section className="border-b border-forest/10 bg-ivory px-6 py-7 md:px-10 lg:px-16" data-id="element-495" data-mp="2m7e4lduqon7">
        <div className="mx-auto flex max-w-7xl flex-wrap justify-center gap-x-8 gap-y-4" data-id="element-496" data-mp="6okhx491gchc">
          {TRUST_ITEMS.map(item => <span key={item.label} className="flex items-center gap-2 text-sm font-medium text-forest/75" data-id="element-497" data-mp="3p18uzwg31ea1">
              <span aria-hidden="true" data-id="element-498" data-mp="3tzk8ch5l66x0">{item.icon}</span>
              {item.label}
            </span>)}
        </div>
      </section>
    </main>;
}